/**
 * Sync Service Types
 */

// Sync manifest from server
export interface SyncManifest {
  userId: string;
  version: number;
  lastModified: string;
  etag: string;
  /** Per-table version numbers - use these for efficient sync detection */
  tableVersions: {
    entries: number;
    periods: number;
    settings: number;
    cgmReadings: number;
    dayNotes: number;
  };
  /** Legacy checksums - kept for backward compatibility */
  checksums: {
    entries: string;
    periods: string;
    settings: string;
    cgmReadings: string;
    dayNotes: string;
  };
  counts: {
    entries: number;
    periods: number;
    cgmReadings: number;
    dayNotes: number;
  };
  /** Archive info for historical data in blob storage. Null if no archives exist. */
  archives?: ArchiveInfo | null;
}

export interface ArchiveInfo {
  activeDataSince?: string;
  tables: Record<string, ArchiveMonthSummary[]>;
}

export interface ArchiveMonthSummary {
  month: string;
  recordCount: number;
  sha256: string;
}

// Result of checking for updates
export interface SyncCheckResult {
  hasChanges: boolean;
  offline?: boolean;
  manifest?: SyncManifest;
  etag?: string;
  error?: string;
}

// Push operation types
export type PushOperationType = "create" | "update" | "delete";

export interface PushOperation {
  type: PushOperationType;
  table: string;
  id: string;
  data: unknown;
  localVersion: number;
  serverVersion?: number;
}

// Push result
export interface PushResult {
  succeeded: Array<{
    table: string;
    id: string;
    serverVersion: number;
  }>;
  conflicts: Array<{
    table: string;
    id: string;
    localData: unknown;
    serverData: unknown;
    serverVersion: number;
  }>;
  errors: Array<{
    table: string;
    id: string;
    error: string;
  }>;
}

// Pull request
export interface PullRequest {
  sinceVersion?: number;
  /** Optional date filter - ISO string. For CGM readings, limits sync to recent data (e.g., last 90 days). */
  sinceDate?: string;
}

// Pull result
export interface PullResult {
  changes: Array<{
    id: string;
    data: unknown;
    version: number;
    isDeleted: boolean;
  }>;
  currentVersion: number;
}

/**
 * Complete data snapshot for monitored users (read-only view for clinicians).
 * Returns all recent data in a single response from GET /api/sync/monitored/{id}/data
 */
export interface MonitoredUserDataResponse {
  userId: string;
  lastModified: string;
  entries: unknown[];
  periods: unknown[];
  settings: unknown[];
  /** CGM readings limited to last 90 days */
  cgmReadings: unknown[];
  /** Day notes (personal and clinic notes attached to days) */
  dayNotes: unknown[];
  counts: {
    entries: number;
    periods: number;
    cgmReadings: number;
    dayNotes: number;
  };
}

// Overall sync result
export interface SyncResult {
  success: boolean;
  skipped?: boolean;
  pushed?: number;
  pulled?: number;
  conflicts?: number;
  error?: string;
}

// Sync state for UI
export interface SyncState {
  status: "idle" | "checking" | "syncing" | "error" | "offline";
  lastSyncTime?: Date;
  pendingChanges: number;
  conflicts: number;
  error?: string;
  /** True after first successful sync completes for this session */
  initialSyncComplete: boolean;
  /** True if server confirmed this user has no data (brand new user) */
  isConfirmedFreshUser: boolean;
}

// Initial sync state
export const INITIAL_SYNC_STATE: SyncState = {
  status: "idle",
  pendingChanges: 0,
  conflicts: 0,
  initialSyncComplete: false,
  isConfirmedFreshUser: false,
};
